import { useEffect, useState } from "react";
import { useModal } from "../contexts";
import { CheckCircleIcon, CodeIcon, ExclamationIcon, PaperAirplaneIcon, XCircleIcon } from "@heroicons/react/outline";
import { Popover } from '@headlessui/react'
import { FormatType } from "../hooks/useRichTextfield";
import { useTranslation } from "react-i18next";
import EmailEditorToolbarButton from "./EmailEditorToolbarButton";
import useUser from "../hooks/useUser";
import ToggleButton from "./ToggleButton";
import PayWallModalPro from "./PayWallModalPro";
import WorkflowModalLink from "./WorkflowModalLink";
import WorkflowModalImage from "./WorkflowModalImage";

import { RichTextFieldMethods } from '../hooks/useRichTextfield';
import { luminance } from "../utils";

interface Props extends RichTextFieldMethods {
  toggleAttachmentMenu: () => void;
  showFormatMenu?: boolean;
  customFormatting?: boolean;
  updateCustomFormating?: (value: boolean) => void;
  sendState?: string;
  saveState: string;
  handleSend?: () => void;
  handleSave: () => void;
  containsCTA?: boolean;
  disabled?: boolean;
}

function EmailEditorToolbar(props: Props) {
  const { user } = useUser();
  const { t } = useTranslation();
  const { openModal } = useModal();
  const [formatMenuOpen, setFormatMenuOpen] = useState(false);

  const [showCTAInserted, setShowCTAInserted] = useState(false);

  const { updateCustomFormating, customFormatting, restoreLastSelection, showFormatMenu = true, applyFormat, addLink, addImage, isHtmlMode, toggleAttachmentMenu, toggleHtmlMode, insertCustom } = props;

  useEffect(() => {
    if (isHtmlMode)
      setFormatMenuOpen(false);
  }, [isHtmlMode]);

  function toggleLinkMenu(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    e.preventDefault();
    restoreLastSelection();

    if (updateCustomFormating) {
      if (!user?.subscription?.featureSignatures)
        return openModal(<PayWallModalPro />);

      if (!customFormatting)
        updateCustomFormating!(true);

      openModal(<WorkflowModalLink onAddLink={addLink} />)
    } else {
      openModal(<WorkflowModalLink onAddLink={(...props) => {
        restoreLastSelection();
        addLink(...props);
      }} />)
    }
  }

  function toggleImageMenu(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    e.preventDefault();

    if (updateCustomFormating) {
      if (!user?.subscription?.featureSignatures)
        return openModal(<PayWallModalPro />);

      if (!customFormatting)
        updateCustomFormating!(true);

      openModal(<WorkflowModalImage addImage={addImage} />)
    } else {
      openModal(<WorkflowModalImage addImage={addImage} />)
    }
  }

  const handleFormatClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    if (isHtmlMode)
      return;

    setFormatMenuOpen(prev => !prev);
  }

  const handleFormatItemClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, format: FormatType) => {
    e.preventDefault();
    applyFormat(format);
  }

  function insertCallToAction() {
    const newCta = document.createElement('a');
    newCta.innerText = 'Review and Sign';

    newCta.id = "cta-button";
    newCta.href = "{{__signature_portal_url}}";

    newCta.style.display = 'block';
    newCta.style.textAlign = 'center';
    newCta.style.width = 'fit-content';
    newCta.style.padding = '10px 20px';
    newCta.style.fontWeight = '600';
    newCta.style.fontWeight = '600';
    newCta.style.borderRadius = '3px';
    newCta.style.border = 'none';
    newCta.style.outline = 'none';
    newCta.style.textDecoration = 'none !important';

    newCta.style.backgroundColor = !user?.activeTeam?.brandingColour || user?.activeTeam?.brandingColour === '#' ? '#1875FB' : user?.activeTeam?.brandingColour;
    newCta.style.color = luminance(user?.activeTeam?.brandingColour ?? '#1875FB') > 0.5 ? 'black' : 'white';

    insertCustom(newCta);

    setShowCTAInserted(true);
    setTimeout(() => setShowCTAInserted(false), 3000);
  }

  return (
    <div className="flex flex-col relative w-full flex-shrink-0 py-2">
      <div className='flex mx-2 gap-2 items-center'>

        {customFormatting !== undefined && updateCustomFormating &&
          <>
            <div className="flex items-center">
              <ToggleButton value={!!customFormatting} onChange={updateCustomFormating} />
              <div className='text-xs text-gray-600 font-gilroy font-semibold ml-2 flex-shrink-0'>
                {t("Custom formatting")}
              </div>
            </div>
            <div className='mx-2 bg-gray-400 h-[20px] w-px my-2' />
          </>
        }

        {showFormatMenu && (
          <>
            <Popover className="relative">
              <EmailEditorToolbarButton
                iconName="format_color_text"
                tooltipText="Formatting options"
                disabled={props.disabled || isHtmlMode || !props.customFormatting}
                isMenu
                onClick={handleFormatClick}
              />
              {formatMenuOpen && (
                <Popover.Panel className="absolute z-10 bottom-full bg-white rounded p-2 flex shadow" static>
                  <EmailEditorToolbarButton
                    onClick={(e) => handleFormatItemClick(e, 'bold')}
                    iconName="format_bold"
                    tooltipText="Bold"
                    disabled={isHtmlMode}
                  />
                  <EmailEditorToolbarButton
                    onClick={(e) => handleFormatItemClick(e, 'italic')}
                    iconName="format_italic"
                    tooltipText="Italic"
                    disabled={isHtmlMode}
                  />
                  <EmailEditorToolbarButton
                    onClick={(e) => handleFormatItemClick(e, 'underline')}
                    iconName="format_underline"
                    tooltipText="Underline"
                    disabled={isHtmlMode}
                  />
                  <div className="h-6 mt-auto mb-auto w-0.5 mx-2 bg-gray-300" />
                  <EmailEditorToolbarButton
                    onClick={(e) => handleFormatItemClick(e, 'justifyLeft')}
                    iconName="format_align_left"
                    tooltipText="Align left"
                    disabled={isHtmlMode}
                  />
                  <EmailEditorToolbarButton
                    onClick={(e) => handleFormatItemClick(e, 'justifyCenter')}
                    iconName="format_align_center"
                    tooltipText="Align center"
                    disabled={isHtmlMode}
                  />
                  <EmailEditorToolbarButton
                    onClick={(e) => handleFormatItemClick(e, 'justifyRight')}
                    iconName="format_align_right"
                    tooltipText="Align right"
                    disabled={isHtmlMode}
                  />
                  <div className="h-6 mt-auto mb-auto w-0.5 mx-2 bg-gray-300" />
                  <EmailEditorToolbarButton
                    onClick={(e) => handleFormatItemClick(e, 'insertUnorderedList')}
                    iconName="format_list_bulleted"
                    tooltipText="Bulleted list"
                    disabled={isHtmlMode}
                  />
                  <EmailEditorToolbarButton
                    onClick={(e) => handleFormatItemClick(e, 'insertOrderedList')}
                    iconName="format_list_numbered"
                    tooltipText="Numbered list"
                    disabled={isHtmlMode}
                  />

                </Popover.Panel>
              )}
            </Popover>
            <EmailEditorToolbarButton iconName="link" tooltipText="Insert link" onClick={toggleLinkMenu} disabled={props.disabled || isHtmlMode || !props.customFormatting} />
            <EmailEditorToolbarButton iconName="insert_photo" tooltipText="Insert photo" onClick={toggleImageMenu} disabled={props.disabled || isHtmlMode || !props.customFormatting} />
            {toggleAttachmentMenu &&
              <EmailEditorToolbarButton iconName="attach_file" tooltipText="Attach file" onClick={toggleAttachmentMenu} disabled={props.disabled} />
            }
          </>
        )}

        {(props.customFormatting) &&
          <>
            <div className='mx-2 bg-gray-400 h-[20px] w-px my-2' />
            <button
              className="flex items-center gap-1 font-semibold font-gilroy text-gray-600 text-sm hover:text-blue hover:underline cursor-pointer shrink-0 mr-6"
              onClick={toggleHtmlMode}
            >
              <CodeIcon className='h-6 w-6' />
              {t("View")} {isHtmlMode ? t('rendered email') : <div className='relative'>{t("source HTML")}</div>}
            </button>
          </>
        }

        <div className="ml-auto flex items-center gap-2">
          {(props.saveState === "INITIAL" && !props.handleSend) &&
            <button className="btn btn-blue w-32" disabled>
              {t("Save")}
            </button>
          }
          {(props.saveState === "HAS_CHANGES" && !props.handleSend) &&
            <button className={`btn btn-blue w-32`} onClick={props.handleSave} disabled={props.disabled}>
              {t("Save")}
            </button>
          }
          {(props.saveState === "HAS_CHANGES" && !!props.handleSend) &&
            <button className={`btn btn-gray w-32 whitespace-nowrap`} onClick={props.handleSave} disabled={props.disabled}>
              {t("Save draft")}
            </button>
          }
          {(props.saveState === "SAVING" && (props.sendState === "INITIAL" || !props.sendState)) &&
            <button className=" btn btn-blue w-32" disabled>
              {t("Saving...")}
            </button>
          }
          {(props.saveState === "SAVED" && (props.sendState === "INITIAL" || !props.sendState)) &&
            <button className=" btn btn-green w-32" disabled>
              {t("Saved")} <CheckCircleIcon className="w-4 h-4 flex-shrink-0 text-green ml-2" />
            </button>
          }

          {!!props.handleSend &&
            <>
              {(props.sendState === "INITIAL") &&
                <button className=" btn btn-purple w-32" onClick={props.handleSend}>
                  {t("Send")} <PaperAirplaneIcon className="w-4 h-4 ml-2 flex-shrink-0 rotate-90" />
                </button>
              }
              {(props.sendState === "SENDING") &&
                <button className=" btn btn-blue w-32" disabled>
                  {t("Sending...")}
                </button>
              }
              {(props.sendState === "SENT") &&
                <button className=" btn btn-green w-32" disabled>
                  {t("Sent")} <CheckCircleIcon className="w-4 h-4 flex-shrink-0 text-green ml-2" />
                </button>
              }
            </>}
        </div>
      </div>

      <div className="flex items-center absolute bottom-16 right-0 left-0">
        {(props.customFormatting && !props.disabled && !props.containsCTA && !showCTAInserted) &&
          <div className={`w-full flex items-center gap-1 text-gray-600 font-semibold text-sm bg-yellow/20 border-yellow rounded flex-shrink-0 h-14 px-2 border-2`}>
            <ExclamationIcon className='w-5 h-5 flex-shrink-0' />
            {t("Please insert the 'Review and Sign' button into the email.")}
            <button className='btn btn-gray ml-auto py-2' onClick={insertCallToAction}>{t("Insert Button")}</button>
          </div>
        }

        {(props.customFormatting && !props.disabled && props.containsCTA && showCTAInserted) &&
          <div className={`w-full flex items-center gap-1 text-gray-600 font-semibold text-sm bg-green/20 border-green rounded flex-shrink-0 h-14 px-2 border-2`}>
            <CheckCircleIcon className='w-5 h-5 flex-shrink-0' />
            {t("This email contains the 'Review and Sign' button.")}
          </div>
        }

        {(props.customFormatting && !props.disabled && !props.containsCTA && showCTAInserted) &&
          <div className={`w-full flex items-center gap-1 text-gray-600 font-semibold text-sm bg-red/20 border-red rounded flex-shrink-0 h-14 px-2 border-2`}>
            <XCircleIcon className='w-5 h-5 flex-shrink-0' />
            {t("Please place your cursor into the email body.")}
          </div>
        }
      </div>
    </div>
  );
}

export default EmailEditorToolbar;
